import { useEffect, useState } from "react";
import { AlertTriangle, Clock, X, RefreshCw, CheckCircle2, XCircle, ExternalLink, AlertCircle } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/AuthProvider";
import { exchangeForLongLivedToken, fetchPageAccessToken } from "../lib/metaTokens";
import { refreshPageAccessToken, refreshInstagramAccessToken } from "../lib/facebookService";
import toast from "react-hot-toast";

interface TokenAccount {
  id: string;
  page_name: string;
  platform: string;
  token_expires_at: string | null;
  source?: "social" | "scheduler";
  daysUntilExpiry?: number;
}

interface RepairResult {
  accountId: string;
  accountName: string;
  platform: string;
  success: boolean;
  error?: string;
  newExpiresAt?: string;
  requiresOAuth?: boolean;
  hint?: string;
}

const EXPIRY_WARNING_DAYS = 7;

// Platforms with auto-renewable refresh tokens - skip expiration warnings for these
const AUTO_RENEWABLE_PLATFORMS = ['youtube', 'tiktok', 'tiktok_business', 'linkedin'];

export function ExpiredTokenBanner() {
  const { user } = useAuth();
  const [expiredAccounts, setExpiredAccounts] = useState<TokenAccount[]>([]);
  const [expiringAccounts, setExpiringAccounts] = useState<TokenAccount[]>([]);
  const [dismissedExpired, setDismissedExpired] = useState(false);
  const [dismissedExpiring, setDismissedExpiring] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isRepairing, setIsRepairing] = useState(false);
  const [repairResults, setRepairResults] = useState<RepairResult[] | null>(null);

  useEffect(() => {
    if (!user) return;

    const checkAccounts = async () => {
      try {
        // Check social_accounts (Social Inbox)
        const { data: socialData, error: socialError } = await supabase
          .from("social_accounts")
          .select("id, page_name, platform, token_expires_at, status, page_id, instagram_account_id")
          .eq("owner_user_id", user.id);

        if (socialError) throw socialError;

        // Check connected_accounts (Scheduler)
        const { data: connectedData, error: connectedError } = await supabase
          .from("connected_accounts")
          .select("id, name, platform, token_expires_at, status")
          .eq("user_id", user.id);

        if (connectedError) throw connectedError;

        const now = new Date();
        const warningThreshold = new Date(now.getTime() + EXPIRY_WARNING_DAYS * 24 * 60 * 60 * 1000);

        const expired: TokenAccount[] = [];
        const expiring: TokenAccount[] = [];

        // Tokeny stron Facebooka / Instagram Business nie maja daty waznosci -
        // Graph API nie zwraca dla nich `expires_in`. Zapisana data jest tylko
        // orientacyjna, wiec dla tych platform o wygasnieciu decyduje wylacznie
        // status ustalany przez validate_meta_tokens() na podstawie Graph API.
        const hasRealExpiry = (platform: string) =>
          platform !== "facebook" && platform !== "instagram";

        // Process social_accounts (skip auto-renewable platforms)
        (socialData || []).forEach((acc) => {
          if (AUTO_RENEWABLE_PLATFORMS.includes(acc.platform)) return;
          const expiresAt = hasRealExpiry(acc.platform) && acc.token_expires_at
            ? new Date(acc.token_expires_at)
            : null;
          const isExpired = acc.status === "expired" || (expiresAt && expiresAt < now);
          const isExpiring = !isExpired && expiresAt && expiresAt < warningThreshold;

          if (isExpired) {
            expired.push({
              id: acc.id,
              page_name: acc.page_name,
              platform: acc.platform,
              token_expires_at: acc.token_expires_at,
              source: "social",
            });
          } else if (isExpiring) {
            const daysLeft = Math.ceil((expiresAt!.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
            expiring.push({
              id: acc.id,
              page_name: acc.page_name,
              platform: acc.platform,
              token_expires_at: acc.token_expires_at,
              source: "social",
              daysUntilExpiry: daysLeft,
            });
          }
        });

        // Process connected_accounts (skip auto-renewable platforms)
        (connectedData || []).forEach((acc) => {
          if (AUTO_RENEWABLE_PLATFORMS.includes(acc.platform)) return;
          const expiresAt = hasRealExpiry(acc.platform) && acc.token_expires_at
            ? new Date(acc.token_expires_at)
            : null;
          const isExpired = acc.status === "expired" || (expiresAt && expiresAt < now);
          const isExpiring = !isExpired && expiresAt && expiresAt < warningThreshold;

          if (isExpired) {
            expired.push({
              id: acc.id,
              page_name: acc.name,
              platform: acc.platform,
              token_expires_at: acc.token_expires_at,
              source: "scheduler",
            });
          } else if (isExpiring) {
            const daysLeft = Math.ceil((expiresAt!.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
            expiring.push({
              id: acc.id,
              page_name: acc.name,
              platform: acc.platform,
              token_expires_at: acc.token_expires_at,
              source: "scheduler",
              daysUntilExpiry: daysLeft,
            });
          }
        });

        // Deduplicate: if a social_accounts entry has a matching connected_accounts entry
        // (same platform + page_id/instagram_account_id), keep only the scheduler version
        // since refreshPageAccessToken works natively with connected_accounts IDs
        // and the sync trigger will cascade changes to social_accounts.
        const schedulerIds = new Set(
          [...expired, ...expiring]
            .filter(a => a.source === 'scheduler')
            .map(a => `${a.platform}:${a.id}`)
        );

        const dedup = (list: TokenAccount[]) => list.filter(acc => {
          if (acc.source !== 'social') return true;
          // Find the original socialData record to get page_id/instagram_account_id
          const socialRec = (socialData || []).find(s => s.id === acc.id);
          if (!socialRec) return true;
          const linkedId = acc.platform === 'facebook' ? socialRec.page_id
            : acc.platform === 'instagram' ? socialRec.instagram_account_id
            : null;
          if (!linkedId) return true;
          return !schedulerIds.has(`${acc.platform}:${linkedId}`);
        });

        setExpiredAccounts(dedup(expired));
        setExpiringAccounts(dedup(expiring));
      } catch (err) {
        console.error("Error checking accounts:", err);
      } finally {
        setLoading(false);
      }
    };

    checkAccounts();

    // Subscribe to changes in both tables
    const socialChannel = supabase
      .channel("expired_accounts_banner_social")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "social_accounts",
          filter: `owner_user_id=eq.${user.id}`,
        },
        () => { checkAccounts(); },
      )
      .subscribe();

    const connectedChannel = supabase
      .channel("expired_accounts_banner_connected")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "connected_accounts",
          filter: `user_id=eq.${user.id}`,
        },
        () => { checkAccounts(); },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(socialChannel);
      supabase.removeChannel(connectedChannel);
    };
  }, [user]);

  if (loading) return null;

  const handleRepairTokens = async (accounts: TokenAccount[]) => {
    setIsRepairing(true);
    const results: RepairResult[] = [];

    for (const account of accounts) {
      try {
        switch (account.platform) {
          case 'facebook': {
            if (account.source === 'social') {
              // For social_accounts: get token & page_id directly from social_accounts table
              // (refreshPageAccessToken only queries connected_accounts and would fail with UUID id)
              const { data: socialAcc } = await supabase
                .from('social_accounts')
                .select('access_token, page_id')
                .eq('id', account.id)
                .single();

              if (socialAcc?.page_id && socialAcc?.access_token) {
                // First try: refresh via connected_accounts if a matching record exists
                let newToken: string | null = null;
                const { data: connectedAcc } = await supabase
                  .from('connected_accounts')
                  .select('id')
                  .eq('id', socialAcc.page_id)
                  .single();

                if (connectedAcc) {
                  // connected_accounts record exists - use standard refresh (it updates connected_accounts,
                  // and the sync trigger will cascade to social_accounts)
                  newToken = await refreshPageAccessToken(socialAcc.page_id);
                }

                if (!newToken) {
                  // Fallback: token strony pobiera funkcja brzegowa tokenem aplikacji
                  try {
                    newToken = await fetchPageAccessToken(socialAcc.page_id);
                  } catch {
                    // Fall through
                  }
                }

                if (newToken) {
                  const expiresAt = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString();
                  // Update social_accounts directly
                  await supabase
                    .from('social_accounts')
                    .update({
                      access_token: newToken,
                      token_expires_at: expiresAt,
                      status: 'active',
                    })
                    .eq('id', account.id);
                  // Also update connected_accounts if exists (trigger may have done this, but ensure consistency)
                  await supabase
                    .from('connected_accounts')
                    .update({
                      access_token: newToken,
                      token_expires_at: expiresAt,
                      status: 'active',
                    })
                    .eq('id', socialAcc.page_id);
                  results.push({
                    accountId: account.id,
                    accountName: account.page_name,
                    platform: account.platform,
                    success: true,
                    newExpiresAt: expiresAt,
                  });
                } else {
                  results.push({
                    accountId: account.id,
                    accountName: account.page_name,
                    platform: account.platform,
                    success: false,
                    error: 'Nie udało się odświeżyć tokena. Token prawdopodobnie całkowicie wygasł.',
                    hint: 'Sprawdź, czy Facebook nie usunął Cię z zarządzania fanpage. Wejdź w fanpage → Ustawienia → Konfiguracja Strony → Dostęp do strony. Jeśli nie widniejesz jako osoba z pełnymi uprawnieniami, poproś właściciela o ponowne dodanie. Następnie podłącz konto ponownie.',
                  });
                }
              } else {
                results.push({
                  accountId: account.id,
                  accountName: account.page_name,
                  platform: account.platform,
                  success: false,
                  error: 'Brak page_id lub tokena dla tego konta.',
                  hint: 'Podłącz konto Facebook ponownie przez stronę Kont.',
                });
              }
            } else {
              // source === 'scheduler' (connected_accounts) - standard flow
              const newToken = await refreshPageAccessToken(account.id);
              if (newToken) {
                results.push({
                  accountId: account.id,
                  accountName: account.page_name,
                  platform: account.platform,
                  success: true,
                  newExpiresAt: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
                });
              } else {
                results.push({
                  accountId: account.id,
                  accountName: account.page_name,
                  platform: account.platform,
                  success: false,
                  error: 'Nie udało się odświeżyć tokena. Token prawdopodobnie całkowicie wygasł.',
                  hint: 'Sprawdź, czy Facebook nie usunął Cię z zarządzania fanpage. Wejdź w fanpage → Ustawienia → Konfiguracja Strony → Dostęp do strony. Jeśli nie widniejesz jako osoba z pełnymi uprawnieniami, poproś właściciela o ponowne dodanie. Następnie podłącz konto ponownie.',
                });
              }
            }
            break;
          }

          case 'instagram': {
            if (account.source === 'social') {
              // For social_accounts: get token directly, then try exchange
              const { data: socialAcc } = await supabase
                .from('social_accounts')
                .select('access_token, instagram_account_id, page_id')
                .eq('id', account.id)
                .single();

              let newToken: string | null = null;

              if (socialAcc?.access_token) {
                // Try to find a matching connected_account to refresh through
                const instagramId = socialAcc.instagram_account_id;
                if (instagramId) {
                  const { data: connectedAcc } = await supabase
                    .from('connected_accounts')
                    .select('id')
                    .eq('id', instagramId)
                    .single();

                  if (connectedAcc) {
                    newToken = await refreshInstagramAccessToken(instagramId);
                  }
                }

                if (!newToken) {
                  // Fallback: wymiane na dlugozyciowy token robi funkcja brzegowa
                  try {
                    newToken = await exchangeForLongLivedToken(socialAcc.access_token, 'instagram');
                  } catch {
                    // Fall through
                  }
                }
              }

              if (newToken) {
                const expiresAt = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString();
                await supabase
                  .from('social_accounts')
                  .update({
                    access_token: newToken,
                    token_expires_at: expiresAt,
                    status: 'active',
                  })
                  .eq('id', account.id);
                // Also update connected_accounts if exists
                if (socialAcc?.instagram_account_id) {
                  await supabase
                    .from('connected_accounts')
                    .update({
                      access_token: newToken,
                      token_expires_at: expiresAt,
                      status: 'active',
                    })
                    .eq('id', socialAcc.instagram_account_id);
                }
                results.push({
                  accountId: account.id,
                  accountName: account.page_name,
                  platform: account.platform,
                  success: true,
                  newExpiresAt: expiresAt,
                });
              } else {
                results.push({
                  accountId: account.id,
                  accountName: account.page_name,
                  platform: account.platform,
                  success: false,
                  error: 'Nie udało się odświeżyć tokena Instagram.',
                  hint: 'Token Instagram jest powiązany z Facebook. Sprawdź uprawnienia do fanpage (Ustawienia → Konfiguracja Strony → Dostęp do strony) i podłącz konto ponownie.',
                });
              }
            } else {
              // source === 'scheduler' - standard flow
              const newToken = await refreshInstagramAccessToken(account.id);
              if (newToken) {
                results.push({
                  accountId: account.id,
                  accountName: account.page_name,
                  platform: account.platform,
                  success: true,
                  newExpiresAt: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
                });
              } else {
                results.push({
                  accountId: account.id,
                  accountName: account.page_name,
                  platform: account.platform,
                  success: false,
                  error: 'Nie udało się odświeżyć tokena Instagram.',
                  hint: 'Token Instagram jest powiązany z Facebook. Sprawdź uprawnienia do fanpage (Ustawienia → Konfiguracja Strony → Dostęp do strony) i podłącz konto ponownie.',
                });
              }
            }
            break;
          }

          case 'instagram_direct': {
            // Try IG Direct refresh endpoint
            const { data: accData } = await supabase
              .from(account.source === 'social' ? 'social_accounts' : 'connected_accounts')
              .select('access_token')
              .eq('id', account.id)
              .single();

            if (accData?.access_token) {
              try {
                const refreshResp = await fetch(
                  `https://graph.instagram.com/refresh_access_token?grant_type=ig_refresh_token&access_token=${accData.access_token}`,
                  { cache: 'no-cache', headers: { 'Accept': 'application/json' } }
                );
                if (refreshResp.ok) {
                  const refreshData = await refreshResp.json();
                  if (refreshData.access_token) {
                    const expiresAt = new Date(Date.now() + (refreshData.expires_in || 5184000) * 1000).toISOString();
                    const table = account.source === 'social' ? 'social_accounts' : 'connected_accounts';
                    await supabase
                      .from(table)
                      .update({
                        access_token: refreshData.access_token,
                        token_expires_at: expiresAt,
                        ...(account.source === 'social' ? { status: 'active' } : {}),
                      })
                      .eq('id', account.id);
                    results.push({
                      accountId: account.id,
                      accountName: account.page_name,
                      platform: account.platform,
                      success: true,
                      newExpiresAt: expiresAt,
                    });
                    break;
                  }
                }
              } catch {
                // Fall through to error
              }
            }
            results.push({
              accountId: account.id,
              accountName: account.page_name,
              platform: account.platform,
              success: false,
              error: 'Nie udało się odświeżyć tokena Instagram Direct.',
              hint: 'Token Instagram Direct wygasł i wymaga ponownego podłączenia konta.',
            });
            break;
          }

          case 'google_business': {
            // Use edge function
            try {
              const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
              const refreshResponse = await fetch(`${supabaseUrl}/functions/v1/google-business-token-refresh`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                },
                body: JSON.stringify({ accountId: account.id }),
              });

              if (refreshResponse.ok) {
                const refreshData = await refreshResponse.json();
                if (refreshData.results?.[0]?.success) {
                  results.push({
                    accountId: account.id,
                    accountName: account.page_name,
                    platform: account.platform,
                    success: true,
                    newExpiresAt: refreshData.results[0].expires_at,
                  });
                  break;
                }
              }
            } catch {
              // Fall through to error
            }
            results.push({
              accountId: account.id,
              accountName: account.page_name,
              platform: account.platform,
              success: false,
              error: 'Nie udało się odświeżyć tokena Google Business.',
              hint: 'Podłącz konto Google Business ponownie.',
            });
            break;
          }

          case 'linkedin': {
            // LinkedIn requires full OAuth re-authorization - can't auto-refresh
            results.push({
              accountId: account.id,
              accountName: account.page_name,
              platform: account.platform,
              success: false,
              requiresOAuth: true,
              error: 'LinkedIn wymaga ponownej autoryzacji OAuth.',
              hint: 'Przejdź do strony Kont i podłącz LinkedIn ponownie.',
            });
            break;
          }

          default: {
            results.push({
              accountId: account.id,
              accountName: account.page_name,
              platform: account.platform,
              success: false,
              error: `Automatyczne odświeżanie nie jest obsługiwane dla platformy ${account.platform}.`,
              hint: 'Podłącz konto ponownie.',
            });
          }
        }
      } catch (error) {
        results.push({
          accountId: account.id,
          accountName: account.page_name,
          platform: account.platform,
          success: false,
          error: error instanceof Error ? error.message : 'Nieznany błąd',
        });
      }
    }

    setIsRepairing(false);
    setRepairResults(results);

    const successCount = results.filter(r => r.success).length;
    if (successCount > 0) {
      toast.success(`Odświeżono ${successCount} z ${results.length} kont`);
    }
  };

  const getPlatformLabel = (platform: string) => {
    const labels: Record<string, string> = {
      facebook: "Facebook",
      instagram: "Instagram",
      instagram_direct: "Instagram Direct",
      linkedin: "LinkedIn",
      tiktok: "TikTok",
      youtube: "YouTube",
      google_business: "Google Business",
      x: "X (Twitter)",
    };
    return labels[platform] || platform;
  };

  const getDaysLabel = (days: number) => {
    if (days <= 0) return "dzisiaj";
    if (days === 1) return "jutro";
    return `za ${days} dni`;
  };

  const navigateToAccounts = () => {
    window.history.pushState({}, "", "/social/accounts");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const showExpired = !dismissedExpired && expiredAccounts.length > 0;
  const showExpiring = !dismissedExpiring && expiringAccounts.length > 0;

  if (!showExpired && !showExpiring && !repairResults) return null;

  const successCount = repairResults?.filter(r => r.success).length ?? 0;
  const failedCount = repairResults ? repairResults.length - successCount : 0;

  return (
    <>
      {/* Red banner - already expired tokens */}
      {showExpired && (
        <div className="bg-red-600 text-white">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 flex-shrink-0 animate-pulse" />
                <div className="flex-1">
                  <p className="font-bold text-lg">⚠️ Wygasły tokeny dostępu!</p>
                  <p className="text-sm text-red-100">
                    {expiredAccounts.length === 1 ? (
                      <>
                        Konto <strong>{expiredAccounts[0].page_name}</strong> (
                        {getPlatformLabel(expiredAccounts[0].platform)}) wymaga
                        ponownej autoryzacji.
                      </>
                    ) : (
                      <>
                        {expiredAccounts.length} kont wymaga ponownej autoryzacji:{" "}
                        {expiredAccounts.slice(0, 3).map((acc, i) => (
                          <span key={acc.id}>
                            {i > 0 && ", "}
                            <strong>{acc.page_name}</strong>
                          </span>
                        ))}
                        {expiredAccounts.length > 3 &&
                          ` i ${expiredAccounts.length - 3} więcej...`}
                      </>
                    )}
                  </p>
                  {expiredAccounts.some(acc => acc.platform === 'facebook' || acc.platform === 'instagram') && (
                    <p className="text-xs text-red-200 mt-1">
                      💡 <strong>Dotyczy Facebook/Instagram?</strong> Sprawdź, czy Facebook nie usunął Cię z zarządzania fanpage.
                      Wejdź w fanpage → <strong>Ustawienia</strong> → <strong>Konfiguracja Strony</strong> → <strong>Dostęp do strony</strong> i upewnij się,
                      że widniejesz jako „Osoba z dostępem do Facebooka" z pełnymi uprawnieniami.
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleRepairTokens(expiredAccounts)}
                  disabled={isRepairing}
                  className="flex items-center gap-2 bg-white text-red-600 px-4 py-2 rounded-lg font-semibold hover:bg-red-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`h-4 w-4 ${isRepairing ? 'animate-spin' : ''}`} />
                  {isRepairing ? 'Naprawiam...' : 'Napraw teraz'}
                </button>
                <button
                  onClick={() => setDismissedExpired(true)}
                  className="p-2 hover:bg-red-500 rounded-lg transition-colors"
                  title="Zamknij na tę sesję"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Amber banner - tokens expiring within 7 days */}
      {showExpiring && (
        <div className="bg-amber-500 text-white">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <Clock className="h-6 w-6 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-bold text-lg">🕐 Tokeny wkrótce wygasną!</p>
                  <p className="text-sm text-amber-100">
                    {expiringAccounts.length === 1 ? (
                      <>
                        Token konta <strong>{expiringAccounts[0].page_name}</strong> (
                        {getPlatformLabel(expiringAccounts[0].platform)}) wygasa{" "}
                        {getDaysLabel(expiringAccounts[0].daysUntilExpiry || 0)}.
                        Odnów połączenie, aby uniknąć przerwy w publikacji.
                      </>
                    ) : (
                      <>
                        {expiringAccounts.length} kont wkrótce straci dostęp:{" "}
                        {expiringAccounts.slice(0, 3).map((acc, i) => (
                          <span key={acc.id}>
                            {i > 0 && ", "}
                            <strong>{acc.page_name}</strong>
                            {" "}({getDaysLabel(acc.daysUntilExpiry || 0)})
                          </span>
                        ))}
                        {expiringAccounts.length > 3 &&
                          ` i ${expiringAccounts.length - 3} więcej...`}
                      </>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleRepairTokens(expiringAccounts)}
                  disabled={isRepairing}
                  className="flex items-center gap-2 bg-white text-amber-600 px-4 py-2 rounded-lg font-semibold hover:bg-amber-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`h-4 w-4 ${isRepairing ? 'animate-spin' : ''}`} />
                  {isRepairing ? 'Odnawiam...' : 'Odnów teraz'}
                </button>
                <button
                  onClick={() => setDismissedExpiring(true)}
                  className="p-2 hover:bg-amber-400 rounded-lg transition-colors"
                  title="Zamknij na tę sesję"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Repair Results Modal */}
      {repairResults && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl ${successCount > 0 && failedCount === 0 ? 'bg-green-100' : failedCount > 0 && successCount === 0 ? 'bg-red-100' : 'bg-amber-100'}`}>
                    {successCount > 0 && failedCount === 0 ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : failedCount > 0 && successCount === 0 ? (
                      <XCircle className="w-6 h-6 text-red-600" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-amber-600" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Wyniki naprawy tokenów</h3>
                    <p className="text-sm text-gray-500">
                      {successCount > 0 && failedCount === 0
                        ? `Wszystkie ${successCount} ${successCount === 1 ? 'konto naprawione' : 'kont naprawionych'} ✓`
                        : successCount > 0
                          ? `Naprawiono ${successCount} z ${repairResults.length} kont`
                          : `Nie udało się naprawić żadnego konta`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setRepairResults(null)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="px-6 py-4 overflow-y-auto max-h-[60vh] space-y-3">
              {repairResults.map((result) => (
                <div
                  key={result.accountId}
                  className={`p-4 rounded-xl border ${
                    result.success
                      ? 'bg-green-50 border-green-200'
                      : result.requiresOAuth
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {result.success ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : result.requiresOAuth ? (
                      <ExternalLink className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900 truncate">{result.accountName}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 flex-shrink-0">
                          {getPlatformLabel(result.platform)}
                        </span>
                      </div>
                      {result.success ? (
                        <p className="text-sm text-green-700">
                          ✅ Token odświeżony pomyślnie
                          {result.newExpiresAt && (
                            <>, ważny do <strong>{new Date(result.newExpiresAt).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })}</strong></>
                          )}
                        </p>
                      ) : (
                        <>
                          <p className="text-sm text-red-700 mb-1">{result.error}</p>
                          {result.hint && (
                            <p className="text-xs text-gray-600 bg-white/60 p-2 rounded-lg mt-2">
                              💡 {result.hint}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              {failedCount > 0 && (
                <button
                  onClick={() => {
                    setRepairResults(null);
                    navigateToAccounts();
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Przejdź do Kont
                </button>
              )}
              <button
                onClick={() => setRepairResults(null)}
                className={`${failedCount > 0 ? 'flex-1' : 'w-full'} px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors`}
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
